import { fetchMyBuilds } from '@/api/builds';
import {
	createSenderPreset,
	fetchSenderPreset,
	updateSenderPreset,
	type SenderRunPresetInput,
} from '@/api/sender-presets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import type { IBuild } from '@/entities/build.entity';
import { $features } from '@/entities/features-entity';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import {
	shouldRequireBuildForCreateRun,
	shouldRequireCaptionForCreateRun,
	type SenderApkDeliveryMethod,
} from '@/lib/sender-apk-delivery';
import { senderRoutes } from '@/lib/sender-routes';
import { cn } from '@/lib/utils';
import { isScenarioDraftDirty } from '@/pages/sender/scenario-dirty';
import SenderBuildPicker from '@/pages/sender/components/sender-build-picker';
import { SenderScreen, SENDER_ADMIN_DISABLED, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useBlocker, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

function draftAfterSave(payload: SenderRunPresetInput): SenderRunPresetInput {
	return {
		...payload,
		caption: payload.caption ?? '',
		buildId: payload.buildId ?? '',
		externalChannelUrl: payload.externalChannelUrl ?? '',
	};
}

const emptyDraft = (): SenderRunPresetInput => ({
	name: '',
	apkDeliveryMethod: 'public_channel',
	caption: '',
	buildId: '',
	externalChannelUrl: '',
	externalChannelMode: 'latest_message',
	audienceFilter: 'contacts_and_dialogs',
	deleteAccountAfterRun: false,
});

export default function SenderScenarioEditorPage() {
	const { senderPresetsEnabled } = useUnit($features);
	const { id } = useParams<{ id?: string }>();
	const editing = Boolean(id && id !== 'new');
	const navigate = useNavigate();
	const [draft, setDraft] = useState<SenderRunPresetInput>(emptyDraft());
	const [initialDraft, setInitialDraft] = useState<SenderRunPresetInput>(emptyDraft());
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [loading, setLoading] = useState(editing);
	const [loadError, setLoadError] = useState(false);
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		if (!senderPresetsEnabled) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setLoadError(false);
		try {
			const buildList = await fetchMyBuilds().catch(() => [] as IBuild[]);
			setBuilds(buildList.filter(build => build.successBuilded));
			if (editing && id) {
				const preset = await fetchSenderPreset(id);
				const next: SenderRunPresetInput = {
					name: preset.name,
					apkDeliveryMethod: preset.apkDeliveryMethod,
					caption: preset.caption ?? '',
					buildId: preset.buildId ?? '',
					externalChannelUrl: preset.externalChannelUrl ?? '',
					externalChannelMode: preset.externalChannelMode,
					audienceFilter: preset.audienceFilter,
					deleteAccountAfterRun: preset.deleteAccountAfterRun,
				};
				setDraft(next);
				setInitialDraft(next);
			}
		} catch {
			setLoadError(true);
		} finally {
			setLoading(false);
		}
	}, [editing, id, senderPresetsEnabled]);

	useEffect(() => {
		void load();
	}, [load]);

	const dirty = isScenarioDraftDirty(draft, initialDraft);
	const blocker = useBlocker(dirty);

	useEffect(() => {
		if (blocker.state !== 'blocked') return;
		const leave = window.confirm('Уйти и не сохранять?');
		if (leave) blocker.proceed();
		else blocker.reset();
	}, [blocker]);

	function canSave(): boolean {
		if (draft.name.trim().length === 0) return false;
		if (draft.apkDeliveryMethod === 'public_channel') {
			if (shouldRequireBuildForCreateRun('public_channel') && !draft.buildId?.trim()) return false;
			if (shouldRequireCaptionForCreateRun('public_channel') && !draft.caption?.trim()) return false;
		}
		if (draft.apkDeliveryMethod === 'external_channel' && !draft.externalChannelUrl?.trim()) return false;
		return true;
	}

	async function save() {
		setSaving(true);
		try {
			const payload: SenderRunPresetInput = {
				...draft,
				name: draft.name.trim(),
				caption: draft.caption?.trim() || null,
				buildId: draft.buildId?.trim() || null,
				externalChannelUrl: draft.externalChannelUrl?.trim() || null,
			};
			if (editing && id) await updateSenderPreset(id, payload);
			else await createSenderPreset(payload);
			const saved = draftAfterSave(payload);
			flushSync(() => {
				setDraft(saved);
				setInitialDraft(saved);
			});
			toast.success('Сценарий сохранён');
			navigate(senderRoutes.scenarios);
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось сохранить'));
		} finally {
			setSaving(false);
		}
	}

	const screenTitle = editing ? 'Сценарий' : 'Новый сценарий';

	if (!senderPresetsEnabled) {
		return (
			<SenderScreen title={screenTitle}>
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			</SenderScreen>
		);
	}

	return (
		<SenderScreen
			title={screenTitle}
			footer={
				<Button
					type='button'
					className={senderPrimaryButtonClassName()}
					disabled={saving || loading || loadError || !canSave()}
					onClick={() => void save()}
				>
					{saving ? <Spinner className='size-4' /> : 'Сохранить'}
				</Button>
			}
		>
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : loadError ? (
				<div className='flex flex-col gap-2'>
					<p className='text-[15px]'>Не удалось загрузить</p>
					<Button type='button' variant='outline' className='min-h-11 w-fit' onClick={() => void load()}>
						Повторить
					</Button>
				</div>
			) : (
				<div className='flex flex-col gap-6'>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='scenario-name'>Название</Label>
						<Input
							id='scenario-name'
							className='min-h-11 text-[15px]'
							value={draft.name}
							onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
						/>
					</div>

					<div className='flex flex-col gap-2'>
						<p className='text-sm font-medium'>Что отправлять</p>
						<DeliveryFields draft={draft} builds={builds} onChange={setDraft} />
					</div>

					<div className='flex flex-col gap-2'>
						<p className='text-sm font-medium'>Кому</p>
						<Choice
							active={draft.audienceFilter === 'contacts_and_dialogs'}
							label='Контакты и диалоги'
							onClick={() => setDraft(prev => ({ ...prev, audienceFilter: 'contacts_and_dialogs' }))}
						/>
						<Choice
							active={draft.audienceFilter === 'mutual_only'}
							label='Только взаимные'
							onClick={() => setDraft(prev => ({ ...prev, audienceFilter: 'mutual_only' }))}
						/>
					</div>

					<div className='flex flex-col gap-2'>
						<p className='text-sm font-medium'>После рассылки</p>
						<div className='flex min-h-11 items-center justify-between gap-3'>
							<Label htmlFor='delete-account'>Удалить сессию после рассылки</Label>
							<Switch
								id='delete-account'
								checked={draft.deleteAccountAfterRun}
								onCheckedChange={checked => setDraft(prev => ({ ...prev, deleteAccountAfterRun: checked }))}
							/>
						</div>
						<p className='text-[13px] text-muted-foreground'>
							{draft.deleteAccountAfterRun
								? 'Сессия удалится после запуска; аккаунт Telegram — через my.telegram.org.'
								: 'Сессия останется после запуска.'}
						</p>
					</div>
				</div>
			)}
		</SenderScreen>
	);
}

function DeliveryFields({
	draft,
	builds,
	onChange,
}: {
	draft: SenderRunPresetInput;
	builds: IBuild[];
	onChange: (next: SenderRunPresetInput) => void;
}) {
	const captionPreview = (draft.caption ?? '').trim();

	function setMethod(method: SenderApkDeliveryMethod) {
		onChange({ ...draft, apkDeliveryMethod: method });
	}
	return (
		<div className='flex flex-col gap-3'>
			<Choice active={draft.apkDeliveryMethod === 'public_channel'} label='Билд и подпись' onClick={() => setMethod('public_channel')} />
			<Choice active={draft.apkDeliveryMethod === 'external_channel'} label='Канал' onClick={() => setMethod('external_channel')} />
			{draft.apkDeliveryMethod === 'public_channel' ? (
				<>
					<SenderBuildPicker
						builds={builds}
						buildId={draft.buildId ?? ''}
						onBuildIdChange={id => onChange({ ...draft, buildId: id })}
					/>
					<Label htmlFor='scenario-caption'>Подпись</Label>
					<Input
						id='scenario-caption'
						className='min-h-11 text-[15px]'
						value={draft.caption ?? ''}
						maxLength={1024}
						onChange={event => onChange({ ...draft, caption: event.target.value })}
					/>
					{captionPreview ? (
						<div className='rounded-md border border-border bg-muted/40 px-3 py-2'>
							<p className='mb-1 text-xs font-medium text-muted-foreground'>Превью подписи</p>
							<p className='text-sm whitespace-pre-wrap break-words'>{captionPreview}</p>
						</div>
					) : null}
				</>
			) : (
				<>
					<Label htmlFor='scenario-channel'>Адрес канала</Label>
					<Input
						id='scenario-channel'
						className='min-h-11 text-[15px]'
						value={draft.externalChannelUrl ?? ''}
						onChange={event => onChange({ ...draft, externalChannelUrl: event.target.value })}
					/>
					<Choice
						active={draft.externalChannelMode === 'latest_message'}
						label='Последнее сообщение'
						onClick={() => onChange({ ...draft, externalChannelMode: 'latest_message' })}
					/>
					<Choice
						active={draft.externalChannelMode === 'post_link'}
						label='Ссылка на пост'
						onClick={() => onChange({ ...draft, externalChannelMode: 'post_link' })}
					/>
				</>
			)}
		</div>
	);
}

function Choice({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
	return (
		<button
			type='button'
			className={cn(
				'min-h-11 rounded-xl border px-3 text-left text-[15px]',
				active ? 'border-primary bg-primary/10 text-primary' : 'border-border',
			)}
			onClick={onClick}
		>
			{label}
		</button>
	);
}
